/**
 * Route -> { title, description } for every public route (see
 * `src/apps/seo/routes.py` on the backend for the same list). Copy is
 * grounded in each page's own hero copy / competitor data / industry
 * heroDescription fields, not invented -- see git blame on this file for the
 * pass that authored it.
 *
 * `title` excludes the " · SecureOneLabs" suffix (added by buildSeoHead).
 * `updated` (optional, YYYY-MM-DD) is the last substantive content change; it is
 * shown under the page overview and emitted as WebPage.dateModified -- bump it
 * whenever the page copy changes.
 */
export const ROUTE_META = {
  '/': {
    title: 'BOSSS: The Cybersecurity ERP — One Security System',
    description: 'BOSSS is the Cybersecurity ERP: 10 integrated modules — GRC, XDR, IAM & more — replacing 8–12 point tools in one system. See the platform demo.',
  },

  // Platform
  '/platform/grc': {
    title: 'GRC Platform — Governance, Risk & Compliance',
    description: 'BOSSS GRC platform automates governance, risk & compliance for NIST, ISO 27001, SOC 2 & HIPAA — natively linked to your XDR, IAM & CMDB. Book a demo.',
    updated: '2026-08-24',
  },
  '/platform/iam': {
    title: 'IAM Platform — Identity & Access Management',
    description: 'BOSSS IAM delivers SSO, MFA, provisioning and identity governance — natively tied to your XDR, GRC and CMDB in one cybersecurity ERP. Book a demo.',
    updated: '2026-08-24',
  },
  '/platform/cmdb': {
    title: 'CMDB Software Built Into Your Security Platform',
    description: 'BOSSS CMDB software auto-discovers 22 asset categories and maps dependencies inside one platform shared with GRC, IAM and XDR. See a live demo.',
    updated: '2026-08-24',
  },
  '/platform/xdr': {
    title: 'XDR Platform — Extended Detection & Response',
    description: 'BOSSS XDR unifies threat detection & response across endpoints, identity & cloud — with 24/7 monitoring and AI agents inside one security ERP. See it live.',
    updated: '2026-08-24',
  },
  '/platform/pentest': {
    title: 'Automated Penetration Testing Platform',
    description: 'Run automated penetration testing continuously: 127 tools, 42 scan types, 16-phase workflow. Findings flow straight into your CMDB, GRC & service desk. Book a demo.',
    updated: '2026-08-24',
  },
  '/platform/service-desk': {
    title: 'IT Service Desk Software With Built-In Security',
    description: 'BOSSS IT service desk software unifies tickets, SLAs and self-service with GRC, IAM and XDR on one platform, so security and IT share one queue. Book a demo.',
    updated: '2026-08-24',
  },
  '/platform/documents': {
    title: 'AI Compliance Document Management Software',
    description: 'BOSSS AI reads, versions and audit-trails every policy and compliance document, sharing one platform with GRC and IAM. Get audit-ready. Book a demo.',
    updated: '2026-08-24',
  },
  '/platform/business': {
    title: 'Business Entity & Process Management for GRC',
    description: 'Map org structure, entities, processes & relationships to risk in one GRC platform. Turn business context into security visibility. See BOSSS in action.',
    updated: '2026-08-24',
  },
  '/platform/resilience': {
    title: 'Business Continuity Software (BCP & DR)',
    description: 'BOSSS business continuity software unifies BCP, disaster recovery, RTO/RPO tracking and crisis management on one risk platform. Plan, test and recover faster.',
    updated: '2026-08-24',
  },
  '/platform/ai-agents': {
    title: 'AI Security Agents | Agentic AI SOC Team',
    description: 'Deploy 9 AI security agents with 440+ tools across GRC, IAM, XDR & more. Voice-first, multilingual, real-time. Your autonomous AI security team. Book a demo.',
    updated: '2026-08-24',
  },

  // Services
  '/services/advisory': {
    title: 'vCISO Services & Strategic Security Consulting',
    description: 'Executive-led vCISO and cybersecurity consulting: compliance roadmaps, risk strategy, and board- and auditor-ready reporting tied to the BOSSS platform.',
    updated: '2026-08-24',
  },
  '/services/implementation': {
    title: 'Security Platform Implementation Services',
    description: 'Expert-led BOSSS deployment in 2–4 weeks: migration, configuration, integration, and team training. Go live fast with a fixed-scope implementation. Get a plan.',
    updated: '2026-08-24',
  },
  '/services/integration': {
    title: 'Security Tool Integration Services | SIEM & API',
    description: 'Connect BOSSS to your existing security stack: SIEM, EDR, and ticketing integrations, custom APIs, and data mapping in 1–3 weeks. No rip-and-replace. Talk to us.',
    updated: '2026-08-24',
  },
  '/services/support': {
    title: 'Enterprise Cybersecurity Support & Customer Success',
    description: 'Enterprise support for BOSSS: dedicated helpdesk, a named customer success manager, priority escalation with SLAs, and proactive health monitoring.',
    updated: '2026-08-24',
  },

  // Industries
  '/industries': {
    title: 'Industries',
    description: 'BOSSS security and compliance solutions built for financial services, healthcare, education, government, manufacturing, retail, and MSSPs.',
  },
  '/industries/financial': {
    title: 'Financial Services Cybersecurity & Compliance',
    description: 'BOSSS unifies GRC, IAM & XDR for financial services, automating SOX, PCI DSS, GLBA & FFIEC compliance in one platform. Protect financial data. Book a demo.',
    updated: '2026-08-24',
  },
  '/industries/healthcare': {
    title: 'Healthcare Cybersecurity & HIPAA Compliance',
    description: 'BOSSS protects PHI with unified cybersecurity for healthcare, automating HIPAA, HITRUST & SOC 2 compliance across GRC, IAM & XDR. See the platform demo.',
    updated: '2026-08-24',
  },
  '/industries/education': {
    title: 'Cybersecurity for Schools: FERPA & GLBA Compliance',
    description: 'Protect student data and meet FERPA, GLBA & state privacy laws with BOSSS — one platform replacing 8-12 security tools, built for lean K-12 and higher-ed budgets.',
    updated: '2026-08-24',
  },
  '/industries/government': {
    title: 'Government Cybersecurity & Compliance Software',
    description: 'BOSSS secures citizen data for government agencies & contractors, mapping FedRAMP, FISMA, NIST 800-53 & CMMC controls in one platform. Request a demo.',
    updated: '2026-08-24',
  },
  '/industries/manufacturing': {
    title: 'OT Security for Manufacturing: IEC 62443 & NIST CSF',
    description: 'Secure converged IT/OT, meet IEC 62443 and NIST CSF, and cut supply-chain risk with BOSSS — one platform for asset visibility, monitoring, and plant resilience.',
    updated: '2026-08-24',
  },
  '/industries/retail': {
    title: 'PCI DSS Compliance for Retailers: v4.0 & CCPA',
    description: 'Meet PCI DSS v4.0 and CCPA, protect payment and customer data, and shrink PCI scope with BOSSS — one platform replacing 8-12 retail security tools. Book a demo.',
    updated: '2026-08-24',
  },
  '/industries/mssp': {
    title: 'MSSP Platform: Multi-Tenant Security Operations',
    description: 'Run every client from one console with BOSSS — multi-tenant GRC, IAM, XDR, and service desk. Deliver 10 security services without stitching 8-12 tools per client.',
    updated: '2026-08-24',
  },

  // Compare
  '/compare': {
    title: 'Compare BOSSS to Other Security Platforms',
    description: 'See how BOSSS compares to ServiceNow, Splunk, CrowdStrike, SailPoint, Archer, and Qualys on cost, deployment time, and platform breadth.',
  },
  '/compare/servicenow': {
    title: 'ServiceNow Alternative: BOSSS Unified Security ERP',
    description: 'Looking for a ServiceNow GRC alternative? Compare BOSSS to ServiceNow GRC & SecOps on cost, deployment time, and unified coverage across GRC, IAM, CMDB, and XDR.',
    updated: '2026-08-24',
  },
  '/compare/splunk': {
    title: 'Splunk Alternative: BOSSS Unified XDR & SecOps',
    description: 'Looking for a Splunk SIEM alternative? Compare BOSSS to Splunk Enterprise Security on cost model, deployment, and the GRC and IAM coverage Splunk lacks.',
    updated: '2026-08-24',
  },
  '/compare/crowdstrike': {
    title: 'CrowdStrike Alternative: BOSSS Unified XDR Platform',
    description: 'Looking for a CrowdStrike alternative? Compare BOSSS to the Falcon platform on endpoint and XDR coverage, cost, and unified GRC and IAM in one security platform.',
    updated: '2026-08-24',
  },
  '/compare/sailpoint': {
    title: 'SailPoint Alternative: BOSSS Unified IGA Platform',
    description: 'Looking for a SailPoint alternative? Compare BOSSS to SailPoint IdentityIQ and Identity Security Cloud on identity governance, cost, and unified GRC, CMDB, and XDR.',
    updated: '2026-08-24',
  },
  '/compare/archer': {
    title: 'Archer Alternative: BOSSS Unified GRC Platform',
    description: 'Looking for an Archer alternative? Compare BOSSS to Archer IRM on deployment time, total cost of ownership, and unified coverage beyond governance and risk.',
    updated: '2026-08-24',
  },
  '/compare/qualys': {
    title: 'Qualys Alternative: BOSSS Unified XDR + VM',
    description: 'Looking for a Qualys alternative? Compare BOSSS to Qualys VMDR on vulnerability management, cost, and the GRC, IAM, and CMDB coverage Qualys alone does not provide.',
    updated: '2026-08-24',
  },

  // Standalone
  '/pricing': {
    title: 'Pricing',
    description: 'Simple, modular, transparent per-user pricing for the BOSSS unified security and compliance platform -- all modules included.',
  },
  '/team': {
    title: 'Team',
    description: 'Meet the SecureOneLabs team building the BOSSS unified security and compliance platform.',
  },
  '/security': {
    title: 'Security By Design',
    description: 'How SecureOneLabs builds, hosts, and secures the BOSSS platform itself -- our approach to security by design.',
  },
  '/learn': {
    title: 'Resources & Learning Center',
    description: 'Guides, product updates, and best practices for GRC, IAM, CMDB, and XDR from the BOSSS resources and learning center.',
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Get in touch with the SecureOneLabs team about the BOSSS unified security and compliance platform.',
  },
  '/demo': {
    title: 'Schedule a Demo',
    description: 'Schedule your BOSSS demo and see the unified security and compliance platform in action for your organization.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'The SecureOneLabs privacy policy: how we collect, use, and protect personal data across the BOSSS platform and website.',
  },
  '/terms-of-service': {
    title: 'Terms of Service',
    description: 'The SecureOneLabs terms of service governing use of the BOSSS platform and website.',
  },
  '/cookie-policy': {
    title: 'Cookie Policy',
    description: 'The SecureOneLabs cookie policy: what cookies BOSSS uses and how to manage your preferences.',
  },
};
